import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {MatChipInputEvent} from '@angular/material/chips';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {separatorKeysCodes} from "@atmi/core/utils";
import {CardType, filePath, SourceArcApiService, SwalService} from "@atmi/core";
import {Nullable, SourceArchivial, SourceArchivialMeta} from "@atmi/core/models";
import {FormManagerService} from "@sim/form";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {Media} from "@sim/media";
import {Status} from "@atmi/models/status";
import {firstValueFrom} from 'rxjs';

export interface IAddArchDialogData {
  objectId: string;
  objectTye: CardType;
}

@Component({
  selector: 'app-add-source-arch-dialog',
  templateUrl: './add-source-arch-dialog.component.html',
  styleUrls: ['./add-source-arch-dialog.component.scss']
})
export class AddSourceArchDialogComponent implements OnInit {

  @Output()
  done: EventEmitter<SourceArchivial | string> = new EventEmitter<SourceArchivial | string>();

  entityForm!: UntypedFormGroup;
  entityControls?: { [P in keyof SourceArchivial]: Nullable<AbstractControl> };

  readonly separatorKeysCodes = separatorKeysCodes;

  constructor(public dialogRef: MatDialogRef<AddSourceArchDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: IAddArchDialogData,
              private readonly sourceArcApi: SourceArcApiService,
              protected readonly formManager: FormManagerService,
              protected readonly route: ActivatedRoute,
              protected readonly router: Router,
              protected readonly snackBarService: MatSnackBar,
              protected readonly spinner: NgxSpinnerService,
              protected readonly translate: TranslateService,
              protected readonly cd: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {

    this.entityForm = this.formManager.makeForm(SourceArchivialMeta);
    this.entityControls = this.formManager.getControls(this.entityForm);

    if (this.cd) {
      this.cd.markForCheck();
    }

  }

  handleDone($event: SourceArchivial | string) {
    if ($event && typeof $event !== 'string') {
      this.dialogRef.close($event);
    }
  }

  async createAndAddToObject() {
    const sourceArc: SourceArchivial = this.entityForm.value;
    this.spinner.show();
    try {
      const createRes = await firstValueFrom(this.sourceArcApi.post(sourceArc));
      this.done.emit(createRes);
      this.handleDone(createRes);
    } catch (err) {
      console.error(err);
      this.handleDone(err.toString());
      this.done.emit(err.toString());
    } finally {
      this.spinner.hide();
    }
  }

  updateImageHandler(media: Media | undefined) {
    if (!this.entityControls) {
      throw new Error('Cannot set image');
    }

    if (!this.entityControls.immagine) {
      throw new Error('Cannot set image without a valid control');
    }

    if (media) {
      this.entityControls.immagine.setValue(media.id);
    } else {
      this.entityControls.immagine.setValue(undefined);
    }
  }

  updateFileHandler(media: Media | undefined) {
    if (!this.entityControls) {
      throw new Error('Cannot set file');
    }

    if (!this.entityControls.file) {
      throw new Error('Cannot set file without a valid control');
    }

    if (media) {
      this.entityControls.file.setValue(media.id);
    } else {
      this.entityControls.file.setValue(undefined);
    }
  }

  removeTagHandler(tagForm: UntypedFormArray, index: number) {
    if (!tagForm.controls) {
      throw new Error('Cannot manage input without a control');
    }

    tagForm.removeAt(index);
  }

  addTagHandler(tagForm: UntypedFormArray, event: MatChipInputEvent, inputTag: HTMLInputElement): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }
    const control = new UntypedFormControl(value);

    tagForm.push(control);

    inputTag.value = '';
  }

  changeStatusHandler(status: Status) {
    if (!this.entityControls) {
      throw new Error('Cannot set status without a valid controls');
    }
    if (!this.entityControls.status) {
      throw new Error('Cannot set status without a valid control');
    }
    this.entityControls.status.setValue(status);
    this.entityControls.status.updateValueAndValidity();
  }

}
